import React from 'react';

import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import moment, {Moment} from 'moment-tz';

import {getDueInTz} from 'app/tasks/utils';

import {CompactTaskItem} from './CompactTaskItem';
import {GroupByDayComponent} from './StyledComponents';

import {SlimTaskInstanceSerializedType} from 'sweet-types';

import type {NotificationType} from 'app/notifly/types';

interface Props {
  tasks: Array<SlimTaskInstanceSerializedType>;
  userTimezone: string;
  notifications: Record<string, Array<NotificationType>>;
}

export const DayGroupedTasksList = ({
  tasks,
  userTimezone,
  notifications,
}: Props): JSX.Element => {
  const now = moment().tz(userTimezone);
  let lastDue: undefined | Moment;

  const groupedByDay = React.useMemo(
    () => groupByDay(tasks, userTimezone),
    [tasks, userTimezone]
  );

  return (
    <>
      {groupedByDay.map(({day, tasks}) => {
        return (
          <GroupByDayComponent day={day} userTimezone={userTimezone} key={day}>
            {tasks.map((task) => {
              const due = getDueInTz(task, userTimezone);
              const showNowLine =
                lastDue && lastDue.isBefore(now) && due.isAfter(now);
              lastDue = due;
              return (
                <CompactTaskItem
                  key={`${task.url}:${due.format()}`}
                  task={task}
                  showNowLine={showNowLine}
                  userTimezone={userTimezone}
                  notifications={notifications[task.url]}
                />
              );
            })}
          </GroupByDayComponent>
        );
      })}
    </>
  );
};

const groupByDay = (
  tasks: Array<SlimTaskInstanceSerializedType>,
  tz: string
): Array<{
  day: string;
  tasks: Array<SlimTaskInstanceSerializedType>;
}> => {
  const orderedDays: string[] = [];

  const daysMap = groupBy(tasks, (task: SlimTaskInstanceSerializedType) => {
    const day = moment.tz(task.due, tz).format('YYMMDD');
    if (orderedDays.indexOf(day) == -1) {
      orderedDays.push(day);
    }
    return day;
  });

  return map(daysMap, (tasks, day) => ({
    day,
    tasks,
  })).sort((a, b) => orderedDays.indexOf(a.day) - orderedDays.indexOf(b.day));
};
